<template>
  <div :class="{'has-error': errorMessage}">
    <!--TEXTAREA : It's a textarea input.-->
    <label class="control-label">{{ pieceName }}</label>
    <p class="piece-instructions" v-if="pieceOptions.instructions" v-html="pieceOptions.instructions"></p>
    <textarea
      v-model="content"
      class="form-control messagearea step-messagearea"
      :name="'pieces[' + pieceIndex + '][content]'"
      ref="textarea"
      @change="$emit('content-change', $event.target.value)"
    ></textarea>
    <template v-if="isRecipients">
      <input v-for="(item, index) in recipientsEmailsList" type="hidden" :name="`pieces[${pieceIndex}][data][emails][${index}]`" :value="item">
    </template>
    <span v-if="validationEnabled && maximumContentLength" class="help-block">
      {{ contentLength }} / {{ maximumContentLength }}
    </span>
    <span v-if="errorMessage" class="text-danger textarea-piece__error">{{ errorMessage }}</span>
    <div class="step-spacer"></div>
  </div>
</template>

<script>
import utils from '../../../../mixins/utils';
import {isValidEmail} from '../../../../utility/isValidEmail.js';

export default {
  name: 'textarea-piece',
  mixins: [utils],
  data() {
    return {
      content: '',
    };
  },
  props: {
    pieceId: {
      type: Number,
      required: true,
    },
    pieceIndex: {
      type: Number,
      required: true,
    },
    pieceName: {
      type: String,
      required: true,
    },
    pieceContent: {
      type: String,
      required: true,
    },
    pieceOptions: {
      type: Object,
      required: true,
    },
    stepActivityType: {
      type: String,
      required: true,
    },
  },
  watch: {
    pieceContent: {
      immediate: true,
      handler(content) {
        this.content = this.stripSlashes(content);
      },
    },
    content: {
      immediate: true,
      handler(val) {
        this.onChange(val);
      },
    },
    errorMessage: {
      immediate: true,
      handler(message) {
        if (this.validationEnabled) {
          this.disableSavingForm(message);
        }
      },
    },
  },
  computed: {
    contentLength() {
      return (this.content && this.content.length) || 0;
    },
    maximumContentLength() {
      const type = String(this.stepActivityType).toLowerCase();

      if (type.includes('facebook')) {
        return 63206;
      } else if (type.includes('linkedin')) {
        return 1300;
      } else if (type.includes('twitter')) {
        return 256;
      } else if (type === 'texts') {
        return 600;
      }
    },
    isPersonalMessage() {
      return String(this.pieceName).toLowerCase().includes('personal message');
    },
    isMessageBody() {
      return String(this.pieceName).toLowerCase().includes('body');
    },
    isRecipients() {
      return String(this.pieceName).toLowerCase().includes('recipients');
    },
    recipientsEmailsList() {
      if (!this.isRecipients) return;
      return this.content
        ?.split('\n')
        .map(email => email.trim())
        .filter(Boolean)
        .filter((value, index, array) => array.indexOf(value) === index);
    },
    validRecipientsEmails() {
      if (!this.isRecipients) return;
      return this.content ? this.recipientsEmailsList.filter(email => isValidEmail(email)) : [];
    },
    recipientsFieldIsValid() {
      if (!this.isRecipients) return true;
      return this.recipientsEmailsList?.length === this.validRecipientsEmails?.length;
    },
    validationEnabled() {
      return (
        ((this.isPersonalMessage || this.isMessageBody) && this.maximumContentLength) ||
        this.isRecipients ||
        this.fieldIsRequired
      );
    },
    fieldIsRequired() {
      return this.pieceOptions.required === 'true';
    },
    errorMessage() {
      if (!this.validationEnabled) {
        return;
      }

      if (this.isPersonalMessage && this.contentLength > this.maximumContentLength) {
        return 'Personal message is too long';
      } else if (this.isMessageBody && this.contentLength > this.maximumContentLength) {
        return 'Message body is too long';
      } else if (this.isMessageBody && this.contentLength === 0) {
        return 'Message body cannot be empty';
      } else if (!this.recipientsFieldIsValid) {
        return 'One of the recipients’ emails seems not to be valid. Please double-check.';
      } else if (this.fieldIsRequired && this.contentLength === 0) {
        return `${this.pieceName.charAt(0).toUpperCase() + this.pieceName.slice(1)} cannot be empty`;
      }
    },
  },
  methods: {
    onChange(value) {
      if (this.isRecipients) {
        this.content = value.toLowerCase();
      }
      if (this.$refs.textarea) {
        this.$refs.textarea.style.height = this.calcHeight(value);
      }
    },
    disableSavingForm(message) {
      this.$emit('disable-saving', {disabled: !!message, pieceId: this.pieceId, message});
    },
    calcHeight(value) {
      let numberOfLineBreaks = (value.match(/\n/g) || []).length;
      let newHeight = 26 + numberOfLineBreaks * 22.857 + 12 + 2;
      newHeight = newHeight < 58 ? 58 : newHeight;
      return newHeight + 'px';
    },
  },
};
</script>

<style scoped lang="scss">
.textarea-piece {
  &__error {
    font-size: 13px;
  }
}
</style>
